import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import PropertyLikeButton from "@/components/common/property-like-button/property-like-button";
import GuestAuthPromptModal from "@/components/common/GuestAuthPromptModal";
import usePropertyLike from "@/hooks/usePropertyLike";
import useOneTapAuth from "@/hooks/useOneTapAuth";
import { useUser } from "@/context/UserContext";
import { getLikeCounts } from "@/services/LikeService";
import {
    getLikeRequestingUserKey,
    getPropertyLikeCountsQueryKey,
    PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
    PROPERTY_LIKE_SIGN_IN_DESCRIPTION,
} from "@/constants/like.constants";
import { MIXPANEL_REGISTER_LOCATION_LIKE_GATE_PROPERTY } from "@/constants/mixpanel.constants";

interface PuzzleListingPropertyLikeProps {
    playablePropertyId: string;
    nicknameLiked?: string | null;
}

/**
 * Property like control on the puzzle listing image slider (heart only, no count).
 */
const PuzzleListingPropertyLike: React.FC<PuzzleListingPropertyLikeProps> = ({
    playablePropertyId,
    nicknameLiked = null,
}) => {
    const { isAuthenticated } = useAuth0();
    const { userState } = useUser();
    const [showGuestAuthPrompt, setShowGuestAuthPrompt] = useState(false);
    const userId = userState?.id ? String(userState.id) : null;
    const requestingUserKey = getLikeRequestingUserKey(userId);
    const isUserAuthenticated = isAuthenticated || Boolean(userState?.is_created);
    const { handleGoogle, handleEmail } = useOneTapAuth({
        redirectPath: window.location.pathname + window.location.search,
        version: "puzzle",
        onClose: () => setShowGuestAuthPrompt(false),
        location: MIXPANEL_REGISTER_LOCATION_LIKE_GATE_PROPERTY,
    });
    const { data: likeCountsData } = useQuery({
        queryKey: getPropertyLikeCountsQueryKey(playablePropertyId, requestingUserKey),
        queryFn: () =>
            getLikeCounts({
                items: [
                    {
                        targetType: PLAYABLE_PROPERTY_LIKE_TARGET_TYPE,
                        targetId: playablePropertyId,
                    },
                ],
                userId: requestingUserKey === "anonymous" ? undefined : requestingUserKey,
            }),
        enabled: Boolean(playablePropertyId),
        retry: false,
    });
    const initialLikeState = useMemo(() => {
        const item = likeCountsData?.counts?.find(
            (entry) => String(entry.targetId) === playablePropertyId,
        );
        return {
            likeCount: item?.count ?? 0,
            likedByCurrentUser: item?.likedByCurrentUser ?? false,
        };
    }, [likeCountsData?.counts, playablePropertyId]);
    const { isLiked, likeCount, isPending: isLikePending, toggleLike } = usePropertyLike({
        playablePropertyId,
        initialLikeCount: initialLikeState.likeCount,
        initialLikedByCurrentUser: initialLikeState.likedByCurrentUser,
        userId,
        isAuthenticated: isUserAuthenticated,
        onRequireAuth: () => setShowGuestAuthPrompt(true),
        mixpanelLocation: "Puzzle Page",
        nicknameLiked,
        contentType: "puzzle",
    });
    return (
        <>
            <PropertyLikeButton
                variant="slider"
                showCount={false}
                isLiked={isLiked}
                likeCount={likeCount}
                isDisabled={isLikePending}
                onClick={(event) => {
                    event.stopPropagation();
                    toggleLike();
                }}
            />
            <GuestAuthPromptModal
                show={showGuestAuthPrompt}
                onClose={() => setShowGuestAuthPrompt(false)}
                onGoogle={handleGoogle}
                onEmail={handleEmail}
                description={PROPERTY_LIKE_SIGN_IN_DESCRIPTION}
                source="like"
            />
        </>
    );
};

export default PuzzleListingPropertyLike;
